import React from "react";

/* ------------------------------------------------------------------
   Docs — Softauto
   Matches Softgraph layout & classnames
   ------------------------------------------------------------------ */

export default function SoftautoDocs() {
  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        {/* Table of contents */}
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#intro">Introduction</a>
          <a href="#why">Why softauto</a>
          <a href="#install">Installation</a>
          <a href="#quickstart">Quickstart</a>
          <a href="#artifacts">Artifacts</a>
          <a href="#advisor">Advisor</a>
          <a href="#imbalance">Imbalance handling</a>
          <a href="#feature-selection">Feature selection</a>
          <a href="#preprocessing">Preprocessing</a>
          <a href="#api">API</a>
          <a href="#recipes">Recipes</a>
          <a href="#serve">Train & serve</a>
          <a href="#faq">FAQ</a>
          <a href="#roadmap">Roadmap</a>
          <a href="#troubleshooting">Troubleshooting</a>
          <a href="#extended">Extended example</a>
          <a href="#examples">Examples</a>
        </aside>

        {/* Main content */}
        <article>
          {/* Hero */}
          <header className="stt-hero">
            <div className="stt-pill">softauto • Docs</div>
            <h1 className="stt-h1">AutoML with seatbelts</h1>
            <p className="stt-lead">
              AutoML that respects small, messy, real-world datasets. One line in → a trained
              pipeline, metrics, and a human-readable report out.
            </p>
          </header>

          {/* Intro */}
          <section id="intro" className="stt-section">
            <h2 className="stt-h2">What is softauto?</h2>
            <p className="stt-p">
              A teaching-friendly AutoML runner for quick, reliable baselines. It performs
              exploratory plots, robust preprocessing, optional feature selection, model
              selection with light tuning, and an accuracy-threshold–based advisor.
            </p>
          </section>

          {/* Why */}
          <section id="why" className="stt-section">
            <h2 className="stt-h2">Why softauto?</h2>
            <ul className="stt-list">
              <li><strong>Advisor mode</strong> – if your model underperforms, it suggests fixes (or auto-switches).</li>
              <li><strong>Safe imbalance handling</strong> – SMOTE only when statistically valid; else class weights.</li>
              <li><strong>Robust preprocessing</strong> – rare-category binning, quantile outlier clipping, flexible scalers.</li>
              <li><strong>Smart feature selection</strong> – Mutual Information &amp; RFECV options.</li>
              <li><strong>Opinionated model zoo</strong> – RF, GB, SVM, KNN, MLP, Ridge/Lasso with curated search spaces.</li>
              <li><strong>Automatic reporting</strong> – HTML + plots (target distribution, missingness, correlations, importances).</li>
              <li><strong>Production artifacts</strong> – reusable <code>pipeline.joblib</code>, <code>metrics.json</code>, <code>tried_models.json</code>.</li>
            </ul>
          </section>

          {/* Install */}
          <section id="install" className="stt-section">
            <h2 className="stt-h2">Installation</h2>
            <div className="stt-code">
              <pre className="stt-pre">{`# From PyPI
pip install softauto

# Optional extras
pip install softauto[boosters]   # xgboost, lightgbm
pip install softauto[imbalance]  # imbalanced-learn (SMOTE)
`}</pre>
            </div>
          </section>

          {/* Quickstart */}
          <section id="quickstart" className="stt-section">
            <h2 className="stt-h2">Quickstart</h2>
            <div className="stt-code">
              <pre className="stt-pre">{`import pandas as pd
from softauto.autorun import AutoRun

# load your dataset
df = pd.read_csv("mydata.csv")

# run softauto
run = AutoRun(df=df, target="label", task="classification")
results = run.fit()

print(results["metrics"])         # final holdout metrics
print(results["best_model_name"]) # chosen model
print(results["artifacts_dir"])   # directory with reports + plots
`}</pre>
            </div>
          </section>

          {/* Artifacts */}
          <section id="artifacts" className="stt-section">
            <h2 className="stt-h2">Artifacts</h2>
            <div className="stt-code">
              <pre className="stt-pre">{`softauto_artifacts/
├─ report.html            # EDA + charts (embedded PNGs)
├─ eda.json               # dataset profile
├─ metrics.json           # holdout metrics (cls: acc/f1/roc_auc; reg: r2/rmse/mae)
├─ tried_models.json      # CV shortlist with scores/hyperparams
├─ model.txt              # best model name
└─ pipeline.joblib        # full sklearn pipeline (preprocess + estimator)
`}</pre>
            </div>
          </section>

          {/* Advisor */}
          <section id="advisor" className="stt-section">
            <h2 className="stt-h2">Advisor (performance guardrails)</h2>
            <p className="stt-p">
              Set <code>advisor_threshold</code> (e.g., 0.75 for classification) to trigger the advisor when CV falls short.
              It returns ranked alternatives with tips, and can auto-switch when <code>advisor_auto_fix=True</code>.
            </p>
            <div className="stt-code">
              <pre className="stt-pre">{`run = AutoRun(..., model="logreg",
              advisor_threshold=0.75, advisor_auto_fix=True)
res = run.fit()
print(res.get("advisor"))
# {
#   "triggered": true,
#   "chosen_model_cv": 0.68,
#   "threshold": 0.75,
#   "suggestions": [["random_forest", 0.78], ["svm_rbf", 0.76]],
#   "tips": ["Try scale='standard' for SVM/MLP", "Increase cv to 7", ...]
# }`}</pre>
            </div>
          </section>

          {/* Imbalance */}
          <section id="imbalance" className="stt-section">
            <h2 className="stt-h2">Imbalance handling (tiny-data-safe)</h2>
            <ul className="stt-list">
              <li><code>imbalance="auto"</code>: class weights by default; enable SMOTE only when each CV fold has ≥ <code>smote_k_neighbors+1</code> minority samples.</li>
              <li>If not safe, it falls back to class weights (warning recorded in <code>eda.json</code>).</li>
            </ul>
            <div className="stt-code">
              <pre className="stt-pre">{`# Controls
imbalance="smote"       # force SMOTE (requires imbalanced-learn)
smote_k_neighbors=2     # lowered default for small datasets
`}</pre>
            </div>
          </section>

          {/* Feature selection */}
          <section id="feature-selection" className="stt-section">
            <h2 className="stt-h2">Feature selection</h2>
            <ul className="stt-list">
              <li><code>feature_selection="mutual_info"</code> + <code>top_k_features</code> — univariate MI ranker (robust on small n).</li>
              <li><code>feature_selection="rfecv"</code> — recursive elimination with cross-validated step sizes.</li>
              <li>Runs inside the pipeline to avoid leakage.</li>
            </ul>
          </section>

          {/* Preprocessing */}
          <section id="preprocessing" className="stt-section">
            <h2 className="stt-h2">Preprocessing (leakage-safe)</h2>
            <ul className="stt-list">
              <li><strong>Numeric:</strong> median imputation → optional clipping via <code>num_clip_quantiles</code> → optional scaler (<code>standard|minmax|robust</code>).</li>
              <li><strong>Categorical:</strong> most-frequent imputation → rare binning with <code>cat_min_freq</code> → one-hot.</li>
              <li>All steps live in a <code>ColumnTransformer</code> inside the final <code>Pipeline</code>, saved as <code>pipeline.joblib</code>.</li>
            </ul>
          </section>

          {/* API */}
          <section id="api" className="stt-section">
            <h2 className="stt-h2">API</h2>
            <div className="stt-code">
              <pre className="stt-pre">{`from softauto.autorun import AutoRun

AutoRun(
  df, target, task="classification",
  model="auto",
  cv=5, tune=None or "random", n_iter=20,
  feature_selection=None or "mutual_info" or "rfecv",
  top_k_features=None,
  imbalance="auto" or "class_weight" or "smote" or None,
  smote_k_neighbors=2,
  numeric_impute="median", categorical_impute="most_frequent",
  scale="standard" or "minmax" or "robust" or None,
  cat_min_freq=0.02,
  num_clip_quantiles=(0.01, 0.99),
  plots=True,
  advisor_threshold=None or float,
  advisor_auto_fix=False,
  report_dir="softauto_artifacts",
)

# .fit() returns:
{
  "metrics": {...},            # task-appropriate metrics
  "cv_best_score": 0.81,
  "best_model_name": "random_forest",
  "artifacts_dir": "softauto_artifacts",
  "pipeline": sklearn.pipeline.Pipeline,
  "advisor": {...} or None
}`}</pre>
            </div>
          </section>

          {/* Recipes */}
          <section id="recipes" className="stt-section">
            <h2 className="stt-h2">Recipes</h2>
            <ul className="stt-list">
              <li><strong>Tiny datasets:</strong> <code>cv=3</code>, <code>imbalance="class_weight"</code> or <code>smote_k_neighbors=2</code>, <code>mutual_info</code> with <code>top_k_features=10–30</code>, prefer <code>logreg</code>/<code>svm_rbf</code>/<code>rf</code>.</li>
              <li><strong>Imbalanced classes:</strong> keep <code>imbalance="auto"</code> (safe) or <code>"smote"</code> if folds are large; raise <code>cv</code>.</li>
              <li><strong>Harder problems:</strong> enable <code>tune="random"</code> with <code>n_iter=30–60</code>, try <code>svm_rbf</code>, <code>rf</code>, <code>gb</code>, <code>mlp</code> (with <code>scale="standard"</code>).</li>
            </ul>
          </section>

          {/* Serve */}
          <section id="serve" className="stt-section">
            <h2 className="stt-h2">Reproducible train &amp; serve</h2>
            <div className="stt-code">
              <pre className="stt-pre">{`from joblib import load
pipe = load("softauto_artifacts/pipeline.joblib")
y_pred = pipe.predict(new_df)
y_prob = getattr(pipe, "predict_proba", lambda X: None)(new_df)
`}</pre>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="stt-section">
            <h2 className="stt-h2">FAQ</h2>
            <ul className="stt-list">
              <li><strong>Can you guarantee ≥ 80% accuracy?</strong> No library can. Use Advisor + tuning + better features.</li>
              <li><strong>Do I need to one-hot/scale myself?</strong> No. The saved <code>pipeline.joblib</code> embeds the full preprocessing graph.</li>
              <li><strong>What about leakage?</strong> All transforms live inside the fitted pipeline and are cross-validated; no peeking.</li>
            </ul>
          </section>

          {/* Roadmap */}
          <section id="roadmap" className="stt-section">
            <h2 className="stt-h2">Roadmap</h2>
            <ul className="stt-list">
              <li>Per-task metric selection &amp; custom metric hooks</li>
              <li>Time-aware CV for temporal splits</li>
              <li>Calibration for probabilistic outputs</li>
              <li>Model cards in <code>report.html</code></li>
            </ul>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshooting" className="stt-section">
            <h2 className="stt-h2">Troubleshooting</h2>
            <ul className="stt-list">
              <li><strong>SMOTE not applied:</strong> fold too small for <code>k_neighbors</code>; lower <code>smote_k_neighbors</code> or use class weights.</li>
              <li><strong>ConvergenceWarning (LogReg/MLP/SVM):</strong> increase <code>max_iter</code>, scale features, or try tree ensembles.</li>
              <li><strong>Low CV stability:</strong> raise <code>cv</code>, reduce <code>top_k_features</code>, or prefer simpler models.</li>
            </ul>
          </section>

          {/* Extended example */}
          <section id="extended" className="stt-section">
            <h2 className="stt-h2">Extended example</h2>
            <div className="stt-code">
              <pre className="stt-pre">{`run = AutoRun(
  df, target="y", task="regression",
  model="auto", cv=7, tune="random", n_iter=48,
  feature_selection="rfecv",
  cat_min_freq=0.03, num_clip_quantiles=(0.02,0.98),
  scale="robust", plots=True,
  advisor_threshold=0.60, advisor_auto_fix=True,
).fit()

print(run["metrics"])  # {'r2': ..., 'rmse': ..., 'mae': ...}
`}</pre>
            </div>
          </section>

          {/* Examples link */}
          <section id="examples" className="stt-section">
            <h2 className="stt-h2">Examples</h2>
            <p className="stt-p">
              Explore live notebooks and snippets in the{" "}
              <a href="/examples/softauto" className="stt-link">examples</a> section. Need data prep first?
              See <a href="/docs/softdata" className="stt-link">softdata</a>.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}
